import React, { useContext, useEffect, useState } from 'react'
import { Input, Spin, App } from 'antd';
import { API, Enum, Regex } from "../../../resources/constants";
import language from "../../../resources/languages/fa_IR";

// Contexts
import AxiosContext from "../../../contexts/axios";

// Interfaces
import { AxiosError } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { TablePaginationConfig } from 'antd/es/table'

const { Pages: { UserManagement: { User: UserLanguage } }, Share, Validation, Message: MessageLanguage } = language
const { USER_MANAGEMENT_USER_ID, USER_MANAGEMENT_USER_CREATE, USER_MANAGEMENT_USER_UPDATE } = API
const { notEnglish, isMobile, isEmail, isNumber } = Regex
const { Message } = Enum

interface UserType {
    row: number,
    id: string,
    key: string,
    firstName: string,
    lastName: string,
    nationalCode: string,
    mobile: string,
    email?: string,
    password?: string,
    newPassword?: string,
    confirm?: string,
    createdAt: Date,
    updatedAt: Date,
}
export default function CURecord(props: { Form: any, form: any, record: { id: string }|null, getUsers: (params: AntTableParams) => void, pagination: TablePaginationConfig, sort: string|false|null|undefined }) {
    const { Form, form, record, getUsers, pagination, sort } = props

    const { axios } = useContext(AxiosContext)
    const [loadingRecord, setLoadingRecord] = useState<boolean>(true);
    const { message } = App.useApp();

    useEffect(() => {
        form.resetFields()
        initialAPI().then(response => response)
    }, []);


    const initialAPI = async () => {
        await getRecord()
    }

    const getRecord = async () => record ? axios.get(USER_MANAGEMENT_USER_ID + record.id).then(async (response: { data: UserType }) => {
        const { data: user } = response
        form.setFieldsValue({ ...user, mobile: user?.mobile ? '0' + user.mobile : undefined })
        setLoadingRecord(false)
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    }) : setLoadingRecord(false)

    const onFinish = ((user: UserType) => {
        setLoadingRecord(true)
        if (record) {
            axios.patch(USER_MANAGEMENT_USER_UPDATE + record.id, { ...user }).then((response: {}) => {
                message.success(MessageLanguage[Message.UPDATE_RECORD]).then(response => response)
                setLoadingRecord(false)
                const { current, pageSize } = pagination
                getUsers({ page: current, limit: pageSize, sort })
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        } else {
            axios.post(USER_MANAGEMENT_USER_CREATE, { ...user, mobile: user.mobile.substring(1) }).then((response: {}) => {
                message.success(MessageLanguage[Message.CREATE_RECORD]).then(response => response)
                form.resetFields()
                setLoadingRecord(false)
                const { current, pageSize } = pagination
                getUsers({ page: current, limit: pageSize, sort })
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        }
    })


    return (
        <Spin spinning={loadingRecord} tip={Share.loading} size="large">
            <Form form={form} name="cu-record" onFinish={onFinish} style={{ margin: '25px 0 10px 0'}}>
                <Form.Item
                    label={UserLanguage.firstName}
                    name="firstName"
                    hasFeedback
                    rules={[
                        { required: true, message: `${UserLanguage.firstName} ${Validation.required}` },
                        { min: 3, message: `${UserLanguage.firstName} ${Validation.min} 3 ${Validation.length}` },
                        { max: 30, message: `${UserLanguage.firstName} ${Validation.max} 30 ${Validation.length}` },
                        { pattern: notEnglish, message: Validation.persian },
                    ]}
                >
                    <Input minLength={3} placeholder={UserLanguage.firstName} />
                </Form.Item>
                <Form.Item
                    label={UserLanguage.lastName}
                    name="lastName"
                    hasFeedback
                    rules={[
                        { required: true, message: `${UserLanguage.lastName} ${Validation.required}` },
                        { min: 3, message: `${UserLanguage.lastName} ${Validation.min} 3 ${Validation.length}` },
                        { max: 30, message: `${UserLanguage.lastName} ${Validation.max} 30 ${Validation.length}` },
                        { pattern: notEnglish, message: Validation.persian },
                    ]}
                >
                    <Input minLength={3} placeholder={UserLanguage.lastName} />
                </Form.Item>
                <Form.Item
                    label={UserLanguage.nationalCode}
                    name="nationalCode"
                    hasFeedback
                    rules={[
                        { required: true, message: `${UserLanguage.nationalCode} ${Validation.required}` },
                        { pattern: isNumber, message: `${UserLanguage.nationalCode} ${Validation.pattern}` },
                        { len: 10, message: `${UserLanguage.nationalCode} 10 ${Validation.length}` },
                    ]}
                >
                    <Input type="tel" maxLength={10} placeholder={UserLanguage.nationalCode} />
                </Form.Item>
                <Form.Item
                    label={UserLanguage.mobile}
                    name="mobile"
                    hasFeedback
                    rules={Boolean(record) ? [] : [
                        { required: true, message: `${UserLanguage.mobile} ${Validation.required}` },
                        { pattern: isMobile, message: Validation.mobile },
                    ]}
                >
                    <Input type="tel" disabled={Boolean(record)} placeholder={UserLanguage.mobile} />
                </Form.Item>
                <Form.Item
                  label={UserLanguage.email}
                  name="email"
                  hasFeedback
                  rules={[
                      { pattern: isEmail, message: Validation.email },
                  ]}
                >
                    <Input disabled={Boolean(record)} placeholder={UserLanguage.email} />
                </Form.Item>
                <Form.Item
                  label={UserLanguage.password}
                  name={Boolean(record) ? 'newPassword' : 'password'}
                  rules={[
                      { required: !Boolean(record), message: `${UserLanguage.password} ${Validation.required}` },
                      { min: 8, message: `${UserLanguage.password} ${Validation.min} 8 ${Validation.length}` },
                  ]}
                >
                    <Input.Password placeholder={UserLanguage.password} />
                </Form.Item>
                <Form.Item
                  label={UserLanguage.confirm}
                  name="confirm"
                  dependencies={[Boolean(record) ? 'newPassword' : 'password']}
                  rules={[
                      { required: !Boolean(record), message: `${UserLanguage.confirm} ${Validation.required}` },
                      ({ getFieldValue }: { getFieldValue: any }) => ({
                          validator(_: any, value: any) {
                              if (!value || getFieldValue(Boolean(record) ? 'newPassword' : 'password') === value) {
                                  return Promise.resolve();
                              }
                              return Promise.reject(new Error(Validation.passwordConfirm));
                          },
                      }),
                  ]}
                >
                    <Input.Password placeholder={UserLanguage.confirm} />
                </Form.Item>
        </Form>
        </Spin>
    )
}
